import { Button, ChevronLeft, useIsScrolled } from '../../../modules/design-system';

export interface DeliveryHeaderProps {
  title: string;
  onBack: () => void;
  backLabel?: string;
}

/**
 * Top bar shared by the Delivery New / Delivery Show pages. Mirrors the
 * MyPacksHeader 3-slot layout: back-pill on the left, centred title,
 * mirrored empty slot on the right (so the title is geometrically
 * centred at @[600px]+).
 *
 * Back-pill uses the shared `Button` with `variant="glass-frosted"` so
 * the Design Constructor's `--co-Button-glass-frosted*` knobs theme it
 * alongside every other heavy-glass surface in the My Packs flow.
 */
export function DeliveryHeader({ title, onBack, backLabel = 'Home' }: DeliveryHeaderProps) {
  const scrolled = useIsScrolled(8);
  return (
    <header
      data-co-component="MyPacksHeader"
      data-scrolled={scrolled || undefined}
      className={
        'sticky top-0 z-30 ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ')
      }
      style={{
        fontFamily:
          'var(--co-MyPacksHeader-fontFamily), Inter, system-ui, sans-serif',
      }}
    >
      <div
        className="
          mx-auto flex w-full max-w-350 items-center gap-3
          px-4 py-4 @[600px]:px-6 @[600px]:py-5
        "
      >
        <div className="flex flex-1 items-center justify-start">
          {/* Mobile: round icon-only back button (Figma 278:7388
           *  pattern). ≥600px keeps the full pill with the label. */}
          <div className="@[600px]:hidden">
            <Button
              variant="glass-icon"
              size="md"
              onClick={onBack}
              aria-label={`Back to ${backLabel}`}
              className="!h-9 !w-9"
            >
              <ChevronLeft size={20} strokeWidth={2} />
            </Button>
          </div>
          <div className="hidden @[600px]:block">
            <Button
              variant="glass-frosted"
              size="lg"
              onClick={onBack}
              aria-label={`Back to ${backLabel}`}
              leftIcon={<ChevronLeft size={24} strokeWidth={2} />}
            >
              {backLabel}
            </Button>
          </div>
        </div>

        <h1
          className="flex-1 text-center text-[18px] font-bold tracking-[-0.3px]"
          style={{ color: 'var(--co-Tabs-tabIdleText, #FFFFFF)' }}
        >
          {title}
        </h1>

        {/* Right spacer mirrors the back-slot width so the title stays
         *  geometrically centred on every breakpoint. */}
        <div aria-hidden className="flex flex-1" />
      </div>
    </header>
  );
}
